import { ChangeDetectionStrategy, Component, HostListener, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';

import { Observable } from 'rxjs';

import {
    ActionItem,
    ApiRecordType,
    BaseContainer,
    DialogConfig,
    DialogRef,
    ErrorResponse,
    FormRadioListItem,
    RecordType,
    RecordTypeStructure,
    RecordTypesService,
    getRecordTypeOptionLabel
} from '@stylehaven/components';
import {
    SelectRecordTypeDialogOutput,
    SelectRecordTypeDialogOutputCancel,
    SelectRecordTypeDialogOutputSuccess
} from '../../../entities/dialog/select-record-type-dialog.entity';
@Component({
    selector: 'mhv-select-record-type-dialog',
    templateUrl: './select-record-type-dialog.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SelectRecordTypeDialogComponent extends BaseContainer implements OnInit {
    public recordTypeOptions: FormRadioListItem<string>[];
    public dialogActionItems$: Observable<ActionItem[]>;
    public disableDropdown$: Observable<boolean>;

    public recordTypeFormGroup: UntypedFormGroup;

    private recordTypeContext: RecordType | undefined;
    private allowedStructures?: RecordTypeStructure[];
    private recordTypes: ApiRecordType[] = [];

    protected readonly ErrorResponse = ErrorResponse;

    constructor(
        public config: DialogConfig,
        public dialog: DialogRef<SelectRecordTypeDialogOutput>,
        private readonly formBuilder: UntypedFormBuilder,
        private readonly recordTypeService: RecordTypesService
    ) {
        super();
    }

    @HostListener('document:keydown.enter', ['$event'])
    handleEnter(event: KeyboardEvent) {
        // Prevent submitting when form is invalid
        if (!this.recordTypeFormGroup.valid) {
            return;
        }
        event.preventDefault();
        this.submit();
    }

    ngOnInit(): void {
        this.recordTypeContext = this.config.data.recordTypeContext;
        this.allowedStructures = this.config.data.allowedStructures ?? undefined;

        this.initForm();
        this.initRecordTypes();
    }

    private initForm(): void {
        this.recordTypeFormGroup = this.formBuilder.group({
            recordType: [null, Validators.required]
        });
    }

    private initRecordTypes(): void {
        if (this.recordTypeContext) {
            this.recordTypes = this.recordTypeService.getAllowedChildren(this.recordTypeContext);
        } else {
            this.recordTypes = this.recordTypeService.getActiveRecordTypes();
        }

        if (this.allowedStructures) {
            this.recordTypes = this.recordTypes.filter(recordType =>
                this.allowedStructures?.includes(recordType.Structure)
            );
        }

        const locale = this.translateService.getCurrentLocale();

        this.recordTypeOptions = this.recordTypes.map(recordType => ({
            id: recordType.Name,
            value: recordType.Name,
            label: getRecordTypeOptionLabel(recordType, locale, this.recordTypes)
        }));

        // If only one option immediately close the dialog
        if (this.recordTypes.length === 1) {
            this.recordTypeControl.setValue(this.recordTypes[0].Name);
            this.submit();
        }
    }

    submit(): void {
        const recordTypeName: string = this.recordTypeControl.value;
        const output: SelectRecordTypeDialogOutputSuccess = {
            type: 'success',
            recordType: this.recordTypes.find(recordType => recordType.Name === recordTypeName)!
        };

        this.dialog.close(output);
    }

    cancel(): void {
        const output: SelectRecordTypeDialogOutputCancel = {
            type: 'cancel'
        };
        this.dialog.close(output);
    }

    get recordTypeControl(): UntypedFormControl {
        return this.recordTypeFormGroup.get('recordType') as UntypedFormControl;
    }
}
